import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const SHOW_DELAY_MS = 250;
const GAP_PX = 4;

/**
 * Хук: показывает кастомный tooltip с полным значением, когда содержимое
 * элемента визуально обрезано (`scrollWidth > clientWidth`).
 *
 * Работает по mouseenter/focus, прячется на mouseleave/blur/scroll/resize.
 * Tooltip рендерится через portal в `document.body` — не клипится
 * `overflow:auto`/`overflow-hidden` родителей (важно для таблиц и виртскролла).
 */
export function useTruncationTooltip(value: string) {
	const ref = useRef<HTMLElement | null>(null);
	const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const show = () => {
		const el = ref.current;
		if (!el || el.scrollWidth <= el.clientWidth) return;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => {
			const rect = el.getBoundingClientRect();
			setPos({ x: rect.left, y: rect.bottom + GAP_PX });
		}, SHOW_DELAY_MS);
	};

	const hide = () => {
		if (timer.current) clearTimeout(timer.current);
		timer.current = null;
		setPos(null);
	};

	useEffect(() => {
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, []);

	useEffect(() => {
		if (!pos) return;
		const onMove = () => setPos(null);
		window.addEventListener("scroll", onMove, true);
		window.addEventListener("resize", onMove);
		return () => {
			window.removeEventListener("scroll", onMove, true);
			window.removeEventListener("resize", onMove);
		};
	}, [pos]);

	const tooltipNode: ReactNode = pos
		? createPortal(
				<div
					role="tooltip"
					className="pointer-events-none fixed z-50 max-w-md break-words rounded bg-slate-900 px-2 py-1 text-xs text-white shadow-lg"
					style={{ left: pos.x, top: pos.y }}
				>
					{value}
				</div>,
				document.body,
			)
		: null;

	return {
		ref,
		triggerProps: {
			onMouseEnter: show,
			onMouseLeave: hide,
			onFocus: show,
			onBlur: hide,
		},
		tooltipNode,
	};
}

interface TruncatedTextProps {
	value: string;
	className?: string;
}

/**
 * Однострочный текст с эллипсисом и tooltip'ом полным значением,
 * когда содержимое визуально обрезано.
 */
export function TruncatedText({ value, className = "" }: TruncatedTextProps) {
	const { ref, triggerProps, tooltipNode } = useTruncationTooltip(value);
	return (
		<>
			<span
				ref={ref as React.Ref<HTMLSpanElement>}
				className={`block truncate ${className}`}
				{...triggerProps}
			>
				{value}
			</span>
			{tooltipNode}
		</>
	);
}
